import React from 'react';

import { Button } from '@/design-system/button';
import { IconButton } from '@/design-system/icon-button';
import type { SelectionGroup } from './useDeliverySelection';

export interface BulkActionBarProps {
  count: number;
  activeGroup: SelectionGroup | null;
  busy?: boolean;
  onClear: () => void;
  onAllocate: () => void;
  onMarkReturned: () => void;
  onMarkFailedReturn: () => void;
  onMarkComplete: () => void;
  onMarkFailed: () => void;
}

/**
 * Floating bar that appears at the bottom of the viewport whenever at
 * least one delivery is selected. Actions are gated by the active
 * selection group per PRD §7.7.
 */
export const BulkActionBar: React.FC<BulkActionBarProps> = ({
  count,
  activeGroup,
  busy = false,
  onClear,
  onAllocate,
  onMarkReturned,
  onMarkFailedReturn,
  onMarkComplete,
  onMarkFailed,
}) => {
  if (count === 0) return null;

  return (
    <div className="fixed inset-x-0 bottom-6 z-40 pointer-events-none flex justify-center px-4">
      <div className="pointer-events-auto flex items-center gap-3 rounded-2xl border border-stroke-soft-200 bg-bg-white-0 px-4 py-2.5 shadow-[0_8px_24px_rgba(23,23,23,0.12),0_2px_8px_rgba(23,23,23,0.06)]">
        {/* Count badge */}
        <div className="flex items-center gap-2 pr-3 border-r border-stroke-soft-200">
          <span className="inline-flex h-6 min-w-[24px] items-center justify-center rounded-md bg-bg-strong-950 px-1.5 text-[12px] font-bold text-text-white-0 tabular-nums">
            {count}
          </span>
          <span className="text-[13px] font-semibold tracking-tight text-text-strong-950">
            selected
          </span>
          {activeGroup && (
            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-text-soft-400">
              · {activeGroup.replace(/_/g, ' ')}
            </span>
          )}
        </div>

        {/* Context-aware actions */}
        <div className="flex items-center gap-2">
          {activeGroup === 'QUEUEING' && (
            <Button size="sm" onClick={onAllocate} disabled={busy}>
              <i className="ri-flashlight-line mr-1.5 text-[14px]" />
              Allocate
            </Button>
          )}

          {activeGroup === 'IN_RETURN' && (
            <>
              <Button
                tone="primary"
                style="stroke"
                size="sm"
                onClick={onMarkReturned}
                disabled={busy}
              >
                <i className="ri-archive-2-line mr-1.5 text-[14px]" />
                Mark as Returned
              </Button>
              <Button
                tone="destructive"
                style="stroke"
                size="sm"
                onClick={onMarkFailedReturn}
                disabled={busy}
              >
                <i className="ri-close-circle-line mr-1.5 text-[14px]" />
                Mark as Failed Return
              </Button>
            </>
          )}

          {(activeGroup === 'PRE_PICKUP' || activeGroup === 'POST_PICKUP') && (
            <>
              <Button
                tone="primary"
                style="stroke"
                size="sm"
                onClick={onMarkComplete}
                disabled={busy}
              >
                <i className="ri-checkbox-circle-line mr-1.5 text-[14px]" />
                Mark as Complete
              </Button>
              <Button
                tone="destructive"
                style="stroke"
                size="sm"
                onClick={onMarkFailed}
                disabled={busy}
              >
                <i className="ri-close-circle-line mr-1.5 text-[14px]" />
                Mark as Failed
              </Button>
            </>
          )}
        </div>

        {/* Clear */}
        <IconButton
          type="button"
          tone="neutral"
          style="ghost"
          size="sm"
          onClick={onClear}
          disabled={busy}
          aria-label="Clear selection"
          className="ml-1"
        >
          <i className="ri-close-line text-[16px]" />
        </IconButton>
      </div>
    </div>
  );
};

export default BulkActionBar;
